/**
 * Journey information panel - slide-in panel on vehicle selection.
 *
 * V1 GPS is always the primary position. V4 and V5 are secondary info sections.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSegmentStore } from '../../stores/segmentStore';
import { useAuthStore } from '../../stores/authStore';
import {
  interpolateSegmentPosition,
  computeFractionFromTimeIntervals,
} from '../../utils/segmentInterpolation';
import { API_CONFIG, QUERY_CONFIG } from '../../config';
import { getDebugCurl } from '../../services/api';
import type { JourneyDetailResponse } from '../../types/api';

/** Haversine distance between two lat/lon points in meters. */
function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Format a millisecond timestamp as HH:MM:SS. */
function fmtTime(ms: number): string {
  if (!ms || ms <= 0) return '--';
  return new Date(ms).toLocaleTimeString();
}

export interface JourneyInfoPanelProps {
  className?: string;
  /** When true, renders without fixed positioning and handlers (for embedding in BottomSheet) */
  embedded?: boolean;
}

async function fetchJourneyDetails(identifier: string): Promise<JourneyDetailResponse> {
  const response = await fetch(
    `${API_CONFIG.REST_BASE}/journeys/${identifier}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch journey: ${response.statusText}`);
  }
  return response.json();
}

export function JourneyInfoPanel({ className = '', embedded = false }: JourneyInfoPanelProps) {
  // V1 GPS is always the primary position — no marker-type switching
  const selectedVehicle = useVehicleStore(
    useShallow((state) => {
      if (!state.selectedVehicleId) return null;
      const vehicle = state.vehicles.get(state.selectedVehicleId);
      if (!vehicle) return null;
      return {
        service_journey_id: vehicle.service_journey_id,
        journey_gid: vehicle.journey_gid,
        latitude: vehicle.latitude,
        longitude: vehicle.longitude,
        speed_kmh: vehicle.speed_kmh,
        heading: vehicle.heading,
        at_stop: vehicle.at_stop,
        data_source: vehicle.data_source,
        line: vehicle.line,
        direction: vehicle.direction,
        time: vehicle.time,
        v4_position: vehicle.v4_position,
        v1LastUpdateTime: vehicle.v1LastUpdateTime,
        v4LastUpdateTime: vehicle.v4LastUpdateTime,
      };
    })
  );
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const isLoggedIn = !!user;
  const segment = useSegmentStore((state) =>
    selectedVehicle ? state.segments.get(selectedVehicle.service_journey_id) : undefined
  );
  const panelRef = useRef<HTMLDivElement>(null);

  // Debug curl copy feedback
  const [curlCopied, setCurlCopied] = useState<'v4' | 'v1da' | null>(null);
  const [curlCopyError, setCurlCopyError] = useState<string | null>(null);
  const [curlCopyErrorType, setCurlCopyErrorType] = useState<'v4' | 'v1da' | null>(null);

  // Shared timer for real-time age display (ticks every second)
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch journey details
  const { data: journeyDetails, isLoading } = useQuery({
    queryKey: ['journey', selectedVehicle?.service_journey_id],
    queryFn: () => fetchJourneyDetails(selectedVehicle!.service_journey_id),
    enabled: !!selectedVehicle,
    staleTime: QUERY_CONFIG.STALE_TIME_MS,
  });

  const handleClose = useCallback(() => {
    selectVehicle(null);
  }, [selectVehicle]);

  // Handle click outside (disabled when embedded in BottomSheet)
  useEffect(() => {
    if (embedded) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        const isMapClick = target.closest('.maplibregl-canvas-container');
        const isControlPanelClick = target.closest('[class*="bg-white"][class*="rounded-lg"][class*="shadow"]');
        if (!isMapClick && !isControlPanelClick) {
          handleClose();
        }
      }
    };
    if (selectedVehicle) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [selectedVehicle, handleClose, embedded]);

  // Handle escape key
  useEffect(() => {
    if (embedded) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    if (selectedVehicle) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedVehicle, handleClose, embedded]);

  // Copy debug curl command to clipboard
  const handleCopyCurl = useCallback(
    async (type: 'v4' | 'v1da') => {
      if (!selectedVehicle) return;
      try {
        const result = await getDebugCurl(
          selectedVehicle.journey_gid,
          type,
          type === 'v4' ? selectedVehicle.latitude : undefined,
          type === 'v4' ? selectedVehicle.longitude : undefined
        );
        const text = result.curl_command;
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for non-secure contexts (HTTP)
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        setCurlCopied(type);
        setTimeout(() => setCurlCopied(null), 2000);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('Failed to copy debug curl:', msg);
        setCurlCopyError(msg);
        setCurlCopyErrorType(type);
        setTimeout(() => { setCurlCopyError(null); setCurlCopyErrorType(null); }, 5000);
      }
    },
    [selectedVehicle]
  );

  if (!selectedVehicle) {
    if (embedded) {
      return (
        <div className={`p-4 text-center text-gray-500 dark:text-gray-400 ${className}`}>
          <p>Tap a vehicle on the map to see details</p>
        </div>
      );
    }
    return null;
  }

  // V1 GPS is always primary — no marker-type switching
  const hasV1Data = selectedVehicle.v1LastUpdateTime != null;
  const hasV4Data = selectedVehicle.v4LastUpdateTime != null;
  const showAtStopBadge = selectedVehicle.at_stop && selectedVehicle.data_source === 'v1-da-gps';

  // V5 interpolation data (computed once, reused in panel)
  const v5Pos = segment ? interpolateSegmentPosition(segment, currentTime) : null;
  const v5Fraction = segment && segment.time_intervals.length >= 4
    ? computeFractionFromTimeIntervals(segment.time_intervals, currentTime)
    : null;
  const v5DeviationM = v5Pos
    ? haversineMeters(selectedVehicle.latitude, selectedVehicle.longitude, v5Pos[0], v5Pos[1])
    : null;

  const containerClasses = embedded
    ? `bg-white dark:bg-gray-800 rounded-lg shadow-xl ${className}`
    : `
        fixed right-4 top-20 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl
        transform transition-transform duration-300 ease-out max-h-[calc(100vh-6rem)] overflow-y-auto
        ${selectedVehicle ? 'translate-x-0' : 'translate-x-full'}
        ${className}
      `;

  return (
    <div
      ref={panelRef}
      className={containerClasses}
      role="dialog"
      aria-label="Journey information"
      aria-modal="false"
    >
      {/* Header */}
      <div
        className="p-4 border-b border-gray-200 dark:border-gray-700"
        style={{
          backgroundColor: selectedVehicle.line?.color_bg || '#666666',
          color: selectedVehicle.line?.color_fg || '#ffffff',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold">
              {selectedVehicle.line?.short_name ?? '?'}
            </span>
            <div className="flex gap-1">
              {isLoggedIn && hasV1Data && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                  V1
                </span>
              )}
              {isLoggedIn && hasV4Data && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                  V4
                </span>
              )}
              {isLoggedIn && segment && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
                  V5
                </span>
              )}
              {showAtStopBadge && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                  At Stop
                </span>
              )}
            </div>
          </div>
          {!embedded && (
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close panel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <p className="text-sm opacity-90 mt-1">
          {selectedVehicle.direction || '--'}
        </p>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Journey ID */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Journey ID</p>
          <p className="font-mono text-sm text-gray-900 dark:text-gray-100">
            {selectedVehicle.journey_gid || 'Unknown'}
          </p>
        </div>

        {/* Primary position — label reflects actual data source */}
        <div className={`border-l-4 rounded-r-lg p-3 ${
          selectedVehicle.data_source === 'v1-da-gps'
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
        }`}>
          <div className="flex items-center justify-between mb-1">
            <p className={`text-xs font-bold uppercase tracking-wider ${
              selectedVehicle.data_source === 'v1-da-gps'
                ? 'text-green-800 dark:text-green-300'
                : 'text-yellow-800 dark:text-yellow-300'
            }`}>
              {selectedVehicle.data_source === 'v1-da-gps' ? 'V1 GPS Position' : 'V4 Estimated Position'}
            </p>
            {selectedVehicle.time && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {(() => {
                  const ts = new Date(selectedVehicle.time);
                  const age = Math.floor((currentTime - ts.getTime()) / 1000);
                  return `${age}s ago`;
                })()}
              </span>
            )}
          </div>
          <p className="font-mono text-sm text-gray-900 dark:text-gray-100">
            {selectedVehicle.latitude.toFixed(5)}, {selectedVehicle.longitude.toFixed(5)}
          </p>
          {selectedVehicle.speed_kmh != null && (
            <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
              <span className="text-gray-500 dark:text-gray-400">Speed:</span>{' '}
              <span className="font-semibold">{selectedVehicle.speed_kmh.toFixed(0)} km/h</span>
            </p>
          )}
        </div>

        {/* V4 Estimated Position (secondary — only when primary is GPS, logged-in users) */}
        {isLoggedIn && selectedVehicle.v4_position && selectedVehicle.data_source === 'v1-da-gps' && (
          <div className="border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 rounded-r-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold uppercase tracking-wider text-yellow-800 dark:text-yellow-300">
                V4 Estimated
              </p>
              {selectedVehicle.v4_position.time && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(() => {
                    const ts = new Date(selectedVehicle.v4_position!.time);
                    const age = Math.floor((currentTime - ts.getTime()) / 1000);
                    return `${age}s ago`;
                  })()}
                </span>
              )}
            </div>
            <p className="font-mono text-sm text-gray-900 dark:text-gray-100">
              {selectedVehicle.v4_position.latitude.toFixed(5)}, {selectedVehicle.v4_position.longitude.toFixed(5)}
            </p>
          </div>
        )}

        {/* V5 Segment (logged-in users, full interpolation data) */}
        {isLoggedIn && segment && (
          <div className="border-l-4 border-purple-500 bg-purple-50 dark:bg-purple-900/20 rounded-r-lg p-3">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-bold uppercase tracking-wider text-purple-800 dark:text-purple-300">
                V5 Segment
              </p>
              <div className="flex items-center gap-1">
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                  segment.state === 'DRIVING'
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                    : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                }`}>
                  {segment.state}
                </span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  segment.has_realtime
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}>
                  {segment.has_realtime ? 'GPS' : 'Sched'}
                </span>
              </div>
            </div>

            {/* From → To */}
            <p className="text-sm text-gray-900 dark:text-gray-100 font-medium mb-2">
              {segment.from_stop.name} → {segment.to_stop.name}
            </p>

            {/* Key metrics: delay, deviation, fraction */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Delay</p>
                <p className={`text-sm font-semibold ${
                  Math.abs(segment.delay_seconds) < 30
                    ? 'text-green-700 dark:text-green-400'
                    : segment.delay_seconds > 0
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-blue-700 dark:text-blue-400'
                }`}>
                  {Math.abs(segment.delay_seconds) < 30
                    ? 'On time'
                    : segment.delay_seconds > 0
                      ? `+${Math.round(segment.delay_seconds)}s`
                      : `${Math.round(segment.delay_seconds)}s`}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Deviation</p>
                <p className={`text-sm font-semibold ${
                  v5DeviationM == null
                    ? 'text-gray-400'
                    : v5DeviationM < 50
                      ? 'text-green-700 dark:text-green-400'
                      : v5DeviationM < 200
                        ? 'text-yellow-700 dark:text-yellow-400'
                        : 'text-red-700 dark:text-red-400'
                }`}>
                  {v5DeviationM != null
                    ? v5DeviationM < 1000
                      ? `${Math.round(v5DeviationM)}m`
                      : `${(v5DeviationM / 1000).toFixed(1)}km`
                    : '--'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">Progress</p>
                <p className="text-sm font-semibold text-purple-700 dark:text-purple-400">
                  {v5Fraction != null ? `${Math.round(v5Fraction * 100)}%` : '--'}
                </p>
              </div>
            </div>

            {/* V5 computed position */}
            {v5Pos && (
              <div className="mb-2">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">V5 Position</p>
                <p className="font-mono text-xs text-gray-900 dark:text-gray-100">
                  {v5Pos[0].toFixed(5)}, {v5Pos[1].toFixed(5)}
                </p>
              </div>
            )}

            {/* Time intervals (geOps-style) */}
            {segment.time_intervals.length >= 4 && (
              <div className="border-t border-purple-200 dark:border-purple-800 pt-2 mt-2">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Time Intervals
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs font-mono text-gray-700 dark:text-gray-300">
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">arr@from:</span>{' '}
                    {fmtTime(segment.time_intervals[0][0])}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">dep@from:</span>{' '}
                    {fmtTime(segment.time_intervals[1][0])}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">arr@to:</span>{' '}
                    {fmtTime(segment.time_intervals[2][0])}
                  </div>
                  <div>
                    <span className="text-gray-400 dark:text-gray-500">dep@to:</span>{' '}
                    {fmtTime(segment.time_intervals[3][0])}
                  </div>
                </div>
              </div>
            )}

            {/* Metadata footer */}
            <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
              {Math.floor((currentTime - segment.receivedAt) / 1000)}s ago
              {' · '}{segment.segment_geometry.length} geom pts
              {segment.data_source && ` · ${segment.data_source}`}
            </p>
          </div>
        )}

        {/* Debug curl commands (admin-only, not in embedded mode) */}
        {isAdmin && !embedded && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Debug Curl
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleCopyCurl('v4')}
                title={curlCopyErrorType === 'v4' && curlCopyError ? curlCopyError : undefined}
                className={`flex-1 text-xs px-2 py-1.5 rounded transition-colors ${
                  curlCopyErrorType === 'v4'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                    : curlCopied === 'v4'
                      ? 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-900 dark:text-yellow-100'
                      : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-900/60'
                }`}
              >
                {curlCopyErrorType === 'v4' ? 'Error (hover)' : curlCopied === 'v4' ? 'Copied!' : 'Copy V4 Curl'}
              </button>
              <button
                onClick={() => handleCopyCurl('v1da')}
                title={curlCopyErrorType === 'v1da' && curlCopyError ? curlCopyError : undefined}
                className={`flex-1 text-xs px-2 py-1.5 rounded transition-colors ${
                  curlCopyErrorType === 'v1da'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                    : curlCopied === 'v1da'
                      ? 'bg-green-200 dark:bg-green-800/60 text-green-900 dark:text-green-100'
                      : 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-900/60'
                }`}
              >
                {curlCopyErrorType === 'v1da' ? 'Error (hover)' : curlCopied === 'v1da' ? 'Copied!' : 'Copy V1-DA Curl'}
              </button>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="text-center py-2">
            <div className="inline-block w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

        {/* Service notes */}
        {journeyDetails?.notes && journeyDetails.notes.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Service Notes
            </p>
            <div className="space-y-2">
              {journeyDetails.notes.map((note, idx) => (
                <div
                  key={idx}
                  className="text-sm p-2 bg-yellow-50 dark:bg-yellow-900/30 border-l-2 border-yellow-400 rounded-r text-gray-900 dark:text-gray-100"
                >
                  {note.message_text}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
